<!-- BagHero — the bag you picked up, large, at the top of the Label sheet and
     the Coffee page.

     The front is the bag itself (BagFront, variant hero) held up in front of a
     soft-focus slice of the wall it came from, with its price clip. Tap it and
     the card turns over (CSS 3D, 500 ms) to the back: the roaster's real
     product photos, a swipeable strip; tap a photo for the full-screen viewer.
     It is a crossfade, not a 3D turn: WebKit mirrored the front and flickered.

     A coffee without product photos has no back, so the bag is not a button. -->
<template>
  <div class="hero hero--still" :class="{ 'hero--back': back, 'hero--tall': tall }">
    <div class="hero__card">
      <!-- the front: the bag -->
      <div class="face face--front" :inert="back || undefined">
        <div class="face__ground" aria-hidden="true">
          <img class="face__plate face__plate--soft" :src="plate" alt="" decoding="async">
          <img class="face__plate" :src="plate" alt="" decoding="async">
        </div>
        <component
          :is="photos.length ? 'button' : 'div'"
          :type="photos.length ? 'button' : undefined"
          class="hold"
          :aria-label="photos.length ? `More photos: ${photos.length} from the roaster` : undefined"
          @click="photos.length && turn(true)"
        >
          <span class="hold__bag" :style="{ '--w': bagWidth }">
            <BagFront :coffee="coffee" :size="grams" variant="hero" :alt="`${coffee.name} by ${coffee.roaster}`" />
          </span>
          <span class="hold__floor" aria-hidden="true" />
        </component>
        <span v-if="price" class="clip" aria-hidden="true">{{ price }}</span>
        <span v-if="photos.length" class="turn" aria-hidden="true">
          <IonIcon :icon="imagesOutline" />
          More photos ({{ photos.length }})
        </span>
      </div>

      <!-- the back: the roaster's photos -->
      <div class="face face--back" :inert="!back || undefined">
        <ul v-if="photos.length" class="strip" :class="{ 'strip--one': photos.length === 1 }" aria-label="Product photos">
          <li v-for="(src, i) in photos" :key="src">
            <button
              type="button"
              class="shot"
              :aria-label="`Open photo ${i + 1} of ${photos.length}`"
              @click="viewer = i"
            >
              <img
                v-if="!failed[src]"
                :src="imgUrl(src, 768)"
                alt=""
                :loading="back ? 'eager' : 'lazy'"
                decoding="async"
                @error="failed[src] = true"
              >
              <span v-else class="shot__none">Photo unavailable</span>
            </button>
          </li>
        </ul>
        <div class="back__bar">
          <span class="back__count">{{ photos.length }} {{ photos.length === 1 ? 'photo' : 'photos' }} from the roaster</span>
          <button ref="backBtn" type="button" class="back__turn" @click="turn(false)">
            <IonIcon :icon="arrowBackOutline" aria-hidden="true" />
            The bag
          </button>
        </div>
      </div>
    </div>

    <PhotoViewer
      :open="viewer >= 0"
      :images="photos"
      :start="Math.max(0, viewer)"
      :title="coffee.name"
      @close="viewer = -1"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, nextTick } from 'vue';
import { IonIcon } from '@ionic/vue';
import { imagesOutline, arrowBackOutline } from 'ionicons/icons';
import { imgUrl } from '../store.js';
import { hash01, frontOf } from '../lib/wall-look.js';
import { haptic } from '../core/util.js';
import BagFront from './BagFront.vue';
import PhotoViewer from './PhotoViewer.vue';
import { money } from './SizePicker.vue';

const props = defineProps({
  coffee: { type: Object, required: true },
  /** The size chosen in the picker ("250g"); sets the pack drawn and the clip's price. */
  size: { type: String, default: '' },
  /** The Coffee page gives the bag more room than the sheet. */
  tall: { type: Boolean, default: false },
});

const BASE = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
// The same four plates the wall is photographed on; a roaster always stands in front of the same one.
const plate = computed(() => `${BASE}/wall/bay-${1 + Math.floor(hash01(props.coffee.roasterSlug || props.coffee.slug) * 4)}.webp`);

const offer = computed(() => props.coffee.offers?.find((o) => o.size === props.size) || props.coffee.offers?.[0] || null);
const grams = computed(() => offer.value?.grams || 250);
const price = computed(() => (offer.value ? `${money(offer.value.price)} · ${offer.value.size}` : ''));
// A bigger pack reads bigger, the way it would in your hand: 250 g is the base.
const bagWidth = computed(() => {
  const k = grams.value >= 1000 ? 1.18 : grams.value >= 500 ? 1.08 : 1;
  return String(k);
});

// Only photos the bag on screen doesn't already show. A real bag front is cut
// from the first photo, so that one is left out; a sticker bag shows its photo
// only as a label, so every photo is worth offering. None left: no button.
const photos = computed(() => {
  const all = props.coffee.images || [];
  return frontOf(props.coffee)?.kind === 'sticker' ? all : all.slice(1);
});
const failed = reactive({});
const back = ref(false);
const viewer = ref(-1);
const backBtn = ref(null);

async function turn (toBack) {
  back.value = toBack;
  haptic('selection');
  await nextTick();
  if (toBack) backBtn.value?.focus({ preventScroll: true });
}

watch(() => props.coffee.slug, () => { back.value = false; viewer.value = -1; });
</script>

<style scoped>
.hero {
  --turn-ms: 500ms;
  --h: calc(var(--sp-8) * 3 + var(--sp-6)); /* 224 */
  perspective: 1400px;
  padding: 0 var(--gutter);
}
.hero--tall { --h: calc(var(--sp-8) * 5 + var(--sp-4)); /* 336 */ }

.hero__card {
  position: relative;
  height: var(--h);
  transform-style: preserve-3d;
  transition: transform var(--turn-ms) cubic-bezier(0.3, 0.7, 0.2, 1);
}
.hero--back .hero__card { transform: rotateY(-180deg); }

.face {
  position: absolute;
  inset: 0;
  overflow: hidden;
  border-radius: var(--radius-lg);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
}
.face--back {
  transform: rotateY(180deg);
  background: var(--paper, #F4EFE6);
  box-shadow: inset 0 0 0 1px var(--line, #ECE6DC);
}

/* Reduced motion: no turn, a crossfade between the faces. */
.hero--still .hero__card { transform: none; transition: none; }
.hero--still .face { backface-visibility: visible; transition: opacity 220ms ease; }
@media (prefers-reduced-motion: reduce) { .hero--still .face { transition-duration: 1ms; } }
.hero--still .face--back { transform: none; opacity: 0; pointer-events: none; }
.hero--still.hero--back .face--back { opacity: 1; pointer-events: auto; }
.hero--still.hero--back .face--front { opacity: 0; }

/* It stands on the middle board of the wall it came off, a step closer to
   you than the shelf: the board sharp under it, the wall behind softening out
   of focus toward the top. */
.face__ground {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: var(--paper, #F4EFE6);
}
.face__plate {
  position: absolute;
  left: 50%;
  top: calc(var(--h) * 0.9);
  width: 190%;
  max-width: none;
  /* The middle board's lip is at 60% of the plate's height. */
  transform: translate(-50%, -60%);
  filter: saturate(0.92);
  -webkit-mask-image: linear-gradient(transparent 52%, #000 57%);
  mask-image: linear-gradient(transparent 52%, #000 57%);
}
.face__plate--soft {
  filter: blur(6px) saturate(0.85) brightness(1.04);
  -webkit-mask-image: none;
  mask-image: none;
}
.face__ground::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(color-mix(in srgb, var(--paper, #F4EFE6) 45%, transparent), transparent 70%);
}

.hold {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 0 calc(var(--h) * 0.125);
  border: 0;
  background: none;
  font: inherit;
  -webkit-tap-highlight-color: transparent;
}
button.hold { cursor: pointer; }
button.hold:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: -4px; border-radius: var(--radius-lg); }

.hold__bag {
  position: relative;
  z-index: 1;
  display: block;
  /* Sized by height: the bag fills ~74% of the card, a 1 kg bag a touch more. */
  height: calc(var(--h) * 0.7 * var(--w));
  max-height: calc(var(--h) * 0.84);
  transform: rotate(-0.8deg);
  transform-origin: 50% 100%;
  transition: transform 260ms var(--ease);
}
.hold__bag :deep(.bag-front) { height: 100%; width: auto; }
button.hold:active .hold__bag { transform: rotate(-0.8deg) scale(0.97); }

/* Contact shadow on the board, the light from the upper right. */
.hold__floor {
  position: absolute;
  left: 50%;
  bottom: calc(var(--h) * 0.11);
  width: 44%;
  height: var(--sp-4);
  transform: translateX(-56%);
  border-radius: 50%;
  background: radial-gradient(closest-side, rgba(40, 26, 12, 0.5), rgba(40, 26, 12, 0));
  filter: blur(2px);
}

/* The price clip, as it hangs on the shelf lip. */
.clip {
  position: absolute;
  z-index: 2;
  left: 50%;
  bottom: calc(var(--h) * 0.035);
  transform: translateX(-50%) rotate(-0.6deg);
  padding: var(--sp-1) var(--sp-2);
  border-radius: 2px;
  background: var(--dock, #FAF7F1);
  color: var(--ink, #1F2A22);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: var(--text-xs);
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  box-shadow: 0 1px 2px rgba(40, 30, 20, 0.22);
  pointer-events: none;
}

.turn {
  position: absolute;
  z-index: 2;
  right: var(--sp-3);
  top: var(--sp-3);
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  padding: var(--sp-1) var(--sp-3);
  border-radius: 999px;
  background: rgba(250, 247, 241, 0.86);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: var(--ink, #1F2A22);
  font-size: var(--text-xs);
  font-weight: 700;
  pointer-events: none;
}
.turn ion-icon { font-size: var(--text-sm); }

/* --- the back --- */
.strip {
  display: flex;
  gap: var(--sp-2);
  height: calc(100% - var(--sp-7) - var(--sp-1));
  margin: 0;
  padding: var(--sp-3) var(--sp-3) 0;
  list-style: none;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: var(--sp-3);
  overscroll-behavior-x: contain;
  scrollbar-width: none;
}
.strip::-webkit-scrollbar { display: none; }
.strip li { flex: none; height: 100%; scroll-snap-align: start; }
.strip--one li { flex: 1; }

.shot {
  display: block;
  height: 100%;
  aspect-ratio: 1;
  padding: 0;
  border: 0;
  border-radius: var(--radius);
  overflow: hidden;
  background: var(--dock, #FAF7F1);
  cursor: zoom-in;
}
.strip--one .shot { width: 100%; aspect-ratio: auto; }
.shot:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; }
.shot img { display: block; width: 100%; height: 100%; object-fit: cover; }
.strip--one .shot img { object-fit: contain; }
.shot__none { display: grid; place-items: center; height: 100%; color: var(--muted, #5E6B60); font-size: var(--text-sm); }

.back__bar {
  position: absolute;
  inset: auto 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-3);
  height: calc(var(--sp-7) + var(--sp-1));
  padding: 0 var(--sp-2) 0 var(--sp-4);
}
.back__count {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--muted, #5E6B60);
  font-family: var(--font-mono, 'Martian Mono', ui-monospace, monospace);
  font-size: calc(var(--text-xs) * 0.9);
  font-weight: 500;
  letter-spacing: 0.02em;
}
.back__turn {
  flex: none;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);
  min-height: var(--tap);
  padding: 0 var(--sp-4);
  border: 0;
  border-radius: 999px;
  background: var(--sage, #E6EDE4);
  color: var(--ink, #1F2A22);
  font: inherit;
  font-size: var(--text-sm);
  font-weight: 700;
  cursor: pointer;
}
.back__turn:focus-visible { outline: 2px solid var(--ink, #1F2A22); outline-offset: 2px; }
</style>
